// src/pages/AccountInactive.tsx
import React from "react";
import { motion } from "framer-motion";
import { MdBlock, MdEmail, MdRefresh } from "react-icons/md";
import { FaExclamationTriangle } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface AccountInactiveProps {
  onContactSupport: () => void;
  onRetryCheck: () => void;
}

const AccountInactive: React.FC<AccountInactiveProps> = ({
  onContactSupport,
  onRetryCheck,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

  const reasons = [
    {
      icon: <MdBlock className="w-4 h-4" />,
      text: t("inactivePage.paymentIssues"),
    },
    {
      icon: <FaExclamationTriangle className="w-4 h-4" />,
      text: t("inactivePage.policyViolation"),
    },
    {
      icon: <MdBlock className="w-4 h-4" />,
      text: t("inactivePage.adminAction"),
    },
    {
      icon: <MdRefresh className="w-4 h-4" />,
      text: t("inactivePage.technicalIssues"),
    },
  ];

  return (
    <div
      className={`min-h-screen py-20 bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4 ${
        isRTL ? "rtl" : "ltr"
      }`}
    >
      <motion.div
        className="max-w-md w-full"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <motion.div className="text-center mb-8" variants={itemVariants}>
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <MdBlock className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t("inactivePage.accountInactive")}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t("inactivePage.accountSuspended")}
          </p>
        </motion.div>

        {/* Main Card */}
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6"
          variants={itemVariants}
        >
          <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
            {t("inactivePage.accountSuspendedDesc")}
          </p>

          {/* Possible Reasons */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              {t("inactivePage.possibleReasons")}
            </h3>
            <ul className="space-y-2">
              {reasons.map((reason, index) => (
                <motion.li
                  key={index}
                  className="flex items-center gap-3 text-gray-600 dark:text-gray-400"
                  variants={itemVariants}
                >
                  <span className="text-red-500 dark:text-red-400">
                    {reason.icon}
                  </span>
                  <span>{reason.text}</span>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* What can you do section */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              {t("inactivePage.whatCanYouDo")}
            </h3>
            <div className="space-y-3">
              <motion.button
                onClick={onContactSupport}
                className="w-full flex items-center gap-3 p-3 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg transition-colors cursor-pointer"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <MdEmail className="w-5 h-5 text-green-600 dark:text-green-400" />
                <div className={`flex-1 ${isRTL ? "text-end" : "text-start"}`}>
                  <div className="font-medium text-green-900 dark:text-green-100">
                    {t("inactivePage.contactSupport")}
                  </div>
                  <div className="text-sm text-green-700 dark:text-green-300">
                    {t("inactivePage.contactSupportDesc")}
                  </div>
                </div>
              </motion.button>

              <motion.button
                onClick={onRetryCheck}
                className="w-full flex items-center gap-3 p-3 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg transition-colors cursor-pointer"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <MdRefresh className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <div className={`flex-1 ${isRTL ? "text-end" : "text-start"}`}>
                  <div className="font-medium text-blue-900 dark:text-blue-100">
                    {t("inactivePage.retryCheck")}
                  </div>
                  <div className="text-sm text-blue-700 dark:text-blue-300">
                    {t("inactivePage.retryCheckDesc")}
                  </div>
                </div>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Support Email */}
        <motion.div
          className="text-center text-gray-600 dark:text-gray-400"
          variants={itemVariants}
        >
          <p className="mb-2">{t("inactivePage.or")}</p>
          <a
            href={`mailto:${t("inactivePage.supportEmail")}`}
            className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium transition-colors"
          >
            {t("inactivePage.supportEmail")}
          </a>
        </motion.div>

        {/* Back to Login */}
        <motion.div className="text-center mt-6" variants={itemVariants}>
          <Link
            to="/login"
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 font-medium transition-colors cursor-pointer"
          >
            {t("inactivePage.backToLogin")}
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AccountInactive;
